import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import type {
  SynthesisSession,
  UpdateSynthesisSessionInput,
} from "../../domain/entities/synthesis-session"
import { useLlmOptions } from "../hooks/use-llm-options"
import { useKnowledgeBases } from "@/pages/knowledge-bases/presentation/hooks/use-knowledge-bases"
import { Spinner } from "@/components/ui/spinner"
import type { KnowledgeBase } from "@/pages/knowledge-bases/domain/entities/knowledge-base"
import { SynthesisSessionSettingsFields } from "./synthesis-session-settings-fields"

type FormProps = {
  session: SynthesisSession
  llmOptions: Array<{ value: string; display: string }>
  kbs: KnowledgeBase[]
  isPending: boolean
  onSave: (input: UpdateSynthesisSessionInput) => void
  onClose: () => void
}

const SessionSettingsForm = ({
  session,
  llmOptions,
  kbs,
  isPending,
  onSave,
  onClose,
}: FormProps) => {
  const c = session.configuration
  const [model, setModel] = useState(() => c?.model ?? "")
  const [kbIds, setKbIds] = useState<string[]>(
    () => c?.knowledgeBaseIds ?? [],
  )
  const handleSave = () => {
    onSave({
      model: model || null,
      knowledgeBaseIds: kbIds,
      enableWebSearch: c?.enableWebSearch ?? false,
    })
  }

  return (
    <>
      <div className="flex shrink-0 gap-2 border-b p-3">
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          className="min-w-0 flex-1"
          onClick={handleSave}
          disabled={isPending}
        >
          {isPending ? "Saving…" : "Save"}
        </Button>
      </div>
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-4">
        <h2 className="mb-4 text-lg font-semibold tracking-tight">
          Conversation settings
        </h2>
        <SynthesisSessionSettingsFields
          idPrefix="synth-session"
          model={model}
          onModelChange={setModel}
          kbIds={kbIds}
          onKbIdsChange={setKbIds}
          llmOptions={llmOptions}
          kbs={kbs}
          enableForm
        />
      </div>
    </>
  )
}

type Props = {
  session: SynthesisSession | null
  open: boolean
  onClose: () => void
  onSave: (input: UpdateSynthesisSessionInput) => void
  isPending: boolean
}

type DialogBodyProps = {
  session: SynthesisSession | null
  onClose: () => void
  onSave: (input: UpdateSynthesisSessionInput) => void
  isPending: boolean
}

const SessionSettingsDialogBody = ({
  session,
  onClose,
  onSave,
  isPending,
}: DialogBodyProps) => {
  const { data: llmOptions, isLoading: llmLoading } = useLlmOptions()
  const { data: kbData, isLoading: kbLoading } = useKnowledgeBases({
    page: 1,
    limit: 100,
  })

  const kbs = kbData?.items ?? []
  const loading = llmLoading || kbLoading
  const options = llmOptions ?? []

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center py-12">
        <Spinner className="size-8" />
      </div>
    )
  }

  if (!session) return null

  return (
    <SessionSettingsForm
      key={session.id}
      session={session}
      llmOptions={options}
      kbs={kbs}
      isPending={isPending}
      onSave={onSave}
      onClose={onClose}
    />
  )
}

export const SessionSettingsDialog = ({
  session,
  open,
  onClose,
  onSave,
  isPending,
}: Props) => {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [open, onClose])

  if (!open) return null

  return createPortal(
    <div
      role="presentation"
      className={cn(
        "fixed inset-0 z-50 bg-black/40",
        "supports-[backdrop-filter]:backdrop-blur-[2px]",
      )}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <aside
        className={cn(
          "fixed top-0 right-0 z-[51] flex h-[100dvh] w-full max-w-sm flex-col border-l bg-background shadow-lg",
        )}
        aria-label="Conversation settings"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <SessionSettingsDialogBody
          session={session}
          onClose={onClose}
          onSave={onSave}
          isPending={isPending}
        />
      </aside>
    </div>,
    document.body,
  )
}
